import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Linking, ActivityIndicator } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { authApi, SmartContact } from '../../shared/utils/api';
import { Ionicons } from '@expo/vector-icons';
import { useToast } from '../toast';

// Icon mapping for contact types
const TYPE_ICONS: Record<string, string> = {
  lender: 'card-outline',
  banker: 'briefcase-outline',
  contractor: 'hammer-outline',
  real_estate_agent: 'home-outline',
  inspector: 'search-outline',
  attorney: 'scale-outline',
  accountant: 'calculator-outline',
  insurance_agent: 'shield-outline',
  property_manager: 'business-outline',
  vendor: 'cube-outline',
  other: 'person-outline',
  surveyor: 'compass-outline',
  title_company: 'document-text-outline',
  septic_service: 'water-outline',
  well_service: 'rainy-outline',
  soil_lab: 'layers-outline',
  environmental: 'leaf-outline',
  forester: 'flower-outline',
  appraiser: 'trending-up-outline',
  engineer: 'construct-outline',
  utility_company: 'flash-outline',
  government: 'business-outline',
  neighbor: 'people-outline',
  farmer_rancher: 'nutrition-outline',
  veterinarian: 'medkit-outline',
  pest_control: 'bug-outline',
};

export interface QuickContactsWidgetProps {
  portfolioId: number;
  propertyId: number;
  category: string; // e.g., 'existing_septic', 'title_search'
  compact?: boolean;
  onAddContact?: () => void;
}

export function QuickContactsWidget({
  portfolioId,
  propertyId,
  category,
  compact = false,
  onAddContact,
}: QuickContactsWidgetProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile } = useResponsive();
  const toast = useToast();
  const widgetStyles = makeWidgetStyles(tokens, utils, isMobile, compact);

  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<SmartContact[]>([]);
  const [suggestedTypes, setSuggestedTypes] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    loadContacts();
  }, [portfolioId, propertyId, category]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await authApi.getSmartContacts(portfolioId, propertyId, { category });
      setContacts(response.contacts.all);
      setSuggestedTypes(response.suggested_types);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (phone: string) => {
    Linking.openURL(`tel:${phone}`);
  };

  const handleEmail = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.showSuccess(`${label} copied`);
      }
    } catch (e) {
      toast.showError('Failed to copy');
    }
  };

  // Group contacts
  const myContacts = contacts.filter(c => c.added_by_info.isCurrentUser);
  const partnerContacts = contacts.filter(c => !c.added_by_info.isCurrentUser);

  if (loading) {
    return (
      <View style={widgetStyles.loadingContainer}>
        <ActivityIndicator size="small" color={tokens.color.primary} />
      </View>
    );
  }

  if (contacts.length === 0 && !compact) {
    return (
      <View style={widgetStyles.emptyContainer}>
        <Text style={widgetStyles.emptyText}>No contacts for this service</Text>
        {suggestedTypes.length > 0 && (
          <Text style={widgetStyles.emptyHint}>
            Add a {suggestedTypes.map(t => t.replace(/_/g, ' ')).join(' or ')}
          </Text>
        )}
      </View>
    );
  }

  if (contacts.length === 0) {
    return null; // In compact mode, hide if no contacts
  }

  const renderContact = (contact: SmartContact, index: number) => {
    const name = contact.company_name || 
      `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 
      'Unnamed';
    const icon = TYPE_ICONS[contact.contact_type.name] || 'person-outline';
    const addedByLabel = contact.added_by_info.isCurrentUser 
      ? 'you' 
      : contact.added_by_info.display_name || contact.added_by_info.email.split('@')[0];

    return (
      <View key={contact.id} style={[widgetStyles.contactRow, index > 0 && widgetStyles.contactRowBorder]}>
        <View style={widgetStyles.contactIcon}>
          <Ionicons name={icon as any} size={16} color={tokens.color.primary} />
        </View>
        <View style={widgetStyles.contactInfo}>
          <Text style={widgetStyles.contactName} numberOfLines={1}>{name}</Text>
          <Text style={widgetStyles.contactAddedBy} numberOfLines={1}>
            Added by {addedByLabel}
          </Text>
        </View>
        <View style={widgetStyles.contactActions}>
          {contact.phone && (
            <Pressable
              onPress={() => handleCall(contact.phone!)}
              onLongPress={() => handleCopy(contact.phone!, 'Phone')}
              style={widgetStyles.actionButton}
            >
              <Ionicons name="call" size={16} color={tokens.color.success} />
            </Pressable>
          )}
          {contact.email && (
            <Pressable
              onPress={() => handleEmail(contact.email!)}
              onLongPress={() => handleCopy(contact.email!, 'Email')}
              style={widgetStyles.actionButton}
            >
              <Ionicons name="mail" size={16} color={tokens.color.primary} />
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={widgetStyles.container}>
      {/* Header */}
      <Pressable 
        onPress={() => setExpanded(!expanded)}
        style={widgetStyles.header}
      >
        <View style={widgetStyles.headerLeft}>
          <Ionicons name="people" size={16} color={tokens.color.primary} />
          <Text style={widgetStyles.headerTitle}>
            Quick Contacts ({contacts.length})
          </Text>
        </View>
        <Ionicons 
          name={expanded ? 'chevron-up' : 'chevron-down'} 
          size={16} 
          color={tokens.color.onSurface} 
        />
      </Pressable>

      {/* Expanded Content */}
      {expanded && (
        <View style={widgetStyles.content}>
          {/* My Contacts */}
          {myContacts.length > 0 && (
            <View style={widgetStyles.section}>
              <Text style={widgetStyles.sectionTitle}>Your Contacts</Text>
              {myContacts.map((c, i) => renderContact(c, i))}
            </View>
          )}

          {/* Partner Contacts */}
          {partnerContacts.length > 0 && (
            <View style={widgetStyles.section}>
              <Text style={widgetStyles.sectionTitle}>Partner Contacts</Text>
              {partnerContacts.map((c, i) => renderContact(c, i))}
            </View>
          )}

          {/* Add Contact Button */}
          {onAddContact && (
            <Pressable onPress={onAddContact} style={widgetStyles.addButton}>
              <Ionicons name="add-circle-outline" size={16} color={tokens.color.primary} />
              <Text style={widgetStyles.addButtonText}>Add Contact</Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
}

function makeWidgetStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  compact: boolean
) {
  return StyleSheet.create({
    container: {
      backgroundColor: addColorOpacity(tokens.color.secondary, 0.05),
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: addColorOpacity(tokens.color.secondary, 0.1),
      overflow: 'hidden',
    },
    loadingContainer: {
      padding: utils.spacing.md,
      alignItems: 'center',
    },
    emptyContainer: {
      padding: utils.spacing.md,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.5),
      fontFamily: utils.typography.body.fontFamily,
    },
    emptyHint: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.4),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: utils.spacing.xs,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.sm,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
    },
    headerTitle: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    content: {
      paddingHorizontal: utils.spacing.sm,
      paddingBottom: utils.spacing.sm,
    },
    section: {
      marginBottom: utils.spacing.sm,
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginBottom: utils.spacing.xs,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    contactRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: utils.spacing.xs,
      gap: utils.spacing.sm,
    },
    contactRowBorder: {
      borderTopWidth: 1,
      borderTopColor: addColorOpacity(tokens.color.border, 0.5),
    },
    contactIcon: {
      width: 28,
      height: 28,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    contactInfo: {
      flex: 1,
      minWidth: 0,
    },
    contactName: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    contactAddedBy: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.5),
      fontFamily: utils.typography.body.fontFamily,
    },
    contactActions: {
      flexDirection: 'row',
      gap: utils.spacing.xs,
    },
    actionButton: {
      width: 32,
      height: 32,
      borderRadius: utils.radius.sm,
      backgroundColor: tokens.color.surface,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    addButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: utils.spacing.xs,
      paddingVertical: utils.spacing.sm,
      marginTop: utils.spacing.xs,
      borderTopWidth: 1,
      borderTopColor: addColorOpacity(tokens.color.border, 0.5),
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    addButtonText: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

